import { NavLink } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Calendar,
  Map,
  Car,
  Tag,
  Activity,
  MapPin,
  Star,
  Image as ImageIcon,
  BookOpen,
  Waves,
  Users,
  FileText,
  Camera,
  Palette,
  ShieldCheck,
  LogOut,
  ExternalLink,
  PanelTop,
  Layout,
  UserCircle,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { User } from "@supabase/supabase-js";

export type AdminSection =
  | "bookings"
  | "custom-trips"
  | "fleet"
  | "pricing"
  | "activities"
  | "destinations"
  | "testimonials"
  | "gallery"
  | "guide"
  | "authors"
  | "content"
  | "photos"
  | "branding"
  | "banners"
  | "footer"
  | "ai-settings"
  | "security";

type SectionGroup = {
  label: string;
  items: { value: AdminSection; label: string; icon: LucideIcon }[];
};

const GROUPS: SectionGroup[] = [
  {
    label: "Transferts",
    items: [
      { value: "bookings", label: "Réservations", icon: Calendar },
      { value: "custom-trips", label: "Voyages sur Mesure", icon: Map },
      { value: "fleet", label: "Flotte", icon: Car },
      { value: "pricing", label: "Tarifs", icon: Tag },
      { value: "activities", label: "Activités", icon: Activity },
      { value: "destinations", label: "Destinations", icon: MapPin },
      { value: "testimonials", label: "Témoignages", icon: Star },
      { value: "gallery", label: "Galerie", icon: ImageIcon },
    ],
  },
  {
    label: "Guide",
    items: [
      { value: "guide", label: "Guide", icon: BookOpen },
      { value: "authors", label: "Auteurs", icon: UserCircle },
    ],
  },
  {
    label: "Site",
    items: [
      { value: "content", label: "Contenu", icon: FileText },
      { value: "photos", label: "Photos", icon: Camera },
      { value: "branding", label: "Identité", icon: Palette },
      { value: "banners", label: "Bannières", icon: PanelTop },
      { value: "footer", label: "Pied de page", icon: Layout },
      { value: "ai-settings", label: "IA & Intégrations", icon: Sparkles },
      { value: "security", label: "Sécurité", icon: ShieldCheck },
    ],
  },
];

interface AdminSidebarProps {
  active: AdminSection;
  onSelect: (s: AdminSection) => void;
  user: User;
  onLogout: () => void;
}

const AdminSidebar = ({ active, onSelect, user, onLogout }: AdminSidebarProps) => {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed";

  const handleSelect = (s: AdminSection) => {
    onSelect(s);
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon" className="!bg-primary text-primary-foreground [&_*]:!border-primary-foreground/10">
      <SidebarContent className="bg-primary text-primary-foreground">
        <div className={`px-3 py-4 ${collapsed ? "text-center" : ""}`}>
          {!collapsed ? (
            <h2 className="font-heading text-lg font-bold text-accent">AdminUp</h2>
          ) : (
            <span className="font-heading text-base font-bold text-accent">A</span>
          )}
        </div>

        {GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            {!collapsed && (
              <SidebarGroupLabel className="text-primary-foreground/60 uppercase tracking-wider text-[10px]">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = active === item.value;
                  return (
                    <SidebarMenuItem key={item.value}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.label}
                        className={`text-primary-foreground/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-accent data-[active=true]:text-accent-foreground`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSelect(item.value)}
                          className="w-full flex items-center gap-2"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
              </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-primary-foreground/10 bg-primary text-primary-foreground">
        {!collapsed && (
          <div className="px-2 py-2 text-xs text-primary-foreground/70 truncate" title={user.email ?? ""}>
            {user.email}
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Aperçu du site" className="text-primary-foreground/90 hover:bg-white/10 hover:text-white">
              <NavLink to="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                {!collapsed && <span>Aperçu du site</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onLogout} tooltip="Déconnexion" className="text-primary-foreground/90 hover:bg-white/10 hover:text-white">
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Déconnexion</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AdminSidebar;